import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader/SectionHeader';
import { defaultDateFormatDateFns } from 'constants/general';
import fetch from 'features/common/fetch';
import { getPpzByLogin } from 'features/other/organizationStructureMUI/ppz/selectors';
import { getSellerByLogin } from 'features/other/organizationStructureMUI/seller/selectors';
import newProductions from 'features/views/productionNew';
import { FIELDS } from 'features/views/productionNew/form';
import user from 'features/views/user';
import { joinString } from 'utils/string';

export const Header = ({ reportedDate, isClosed, editMode, organisationStructure, mainFilterFormData: formData }) => {
	const dispatch = useDispatch();
	const sellerIns = useSelector(newProductions.selectors.getSellerNumbers);
	const seller = useSelector(getSellerByLogin(formData?.seller));
	const ppz = useSelector(getPpzByLogin(formData?.ppz));

	const roles = useSelector(user.selectors.getRolesByCurrentRole);
	const defaultUserLogin = useSelector(user.selectors.getUser);
	const defaultUser = useMemo(() => ({ ...roles[0], login: defaultUserLogin }), [roles, defaultUserLogin]);

	useEffect(() => {
		if (editMode) {
			// in edit mode i will take user from selected production/document
			const user = organisationStructure?.ppz || organisationStructure?.seller;
			dispatch(newProductions.actions.loadUsersSellerNumbers(user?.login));
		} else {
			// otherwise lowers role from form
			const user = ppz || seller || defaultUser;
			dispatch(newProductions.actions.loadUsersSellerNumbers(user.login));
		}
	}, [dispatch, seller, ppz, organisationStructure, editMode, defaultUser]);

	const reportDate = reportedDate ? new Date(reportedDate) : new Date();
	const formattedReportDate = format(reportDate, defaultDateFormatDateFns);

	const generateSellerInsOptions = useCallback(
		() => sellerIns.map((sellerIn) => ({ name: String(sellerIn), code: sellerIn })),
		[sellerIns],
	);

	// because of "required" param
	const renderInput = useCallback(
		(params) => (
			<TextField label={getLabelMessage('producedProductionNew:sellerIn')} fullWidth {...params} required />
		),
		[],
	);

	return (
		<Stack spacing={1}>
			<SectionHeader
				text={joinString(' ', [getLabelMessage('producedProductionNew:subTitle'), formattedReportDate])}
				variant="h5"
			/>
			<Grid container>
				<Grid size={12}>
					<Grid container>
						<Grid size={4}>
							<ComponentLoader fetchStorePath={fetch.constants.urls.userSellerIns}>
								<Autocomplete
									size="small"
									fieldName={FIELDS.sellerIn}
									options={generateSellerInsOptions(sellerIns)}
									labelPropName="name"
									valuePropName="code"
									label={getLabelMessage('producedProductionNew:sellerIn')}
									disabled={sellerIns.length === 1 || isClosed}
									renderInput={renderInput}
								/>
							</ComponentLoader>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Stack>
	);
};

Header.propTypes = {
	editMode: PropTypes.bool,
	isClosed: PropTypes.bool,
	mainFilterFormData: PropTypes.object,
	organisationStructure: PropTypes.object,
	reportedDate: PropTypes.string,
};

export default Header;
